import { useState } from 'react';
import {
  Wrench,
  AlertTriangle,
  CheckCircle,
  CheckCircle2,
  ExternalLink,
  Play,
  X,
  Info,
  Globe,
  Shield,
  Clock,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/tamagui';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Badge } from '@/components/ui/Badge';
import RiskBadge from './RiskBadge';

type RiskLevel = 'critical' | 'high' | 'medium' | 'low' | 'info';

interface OriginAnalysis {
  verified: boolean;
  source: string;
  author: string;
  repository?: string;
  lastUpdated: string;
  securityScore: number;
  knownVulnerabilities: string[];
  trustIndicators: string[];
}

interface ToolInfo {
  name: string;
  description: string;
  riskLevel: RiskLevel;
  origin?: string;
  capabilities?: string[];
  originAnalysis?: OriginAnalysis;
}

interface MCPToolsSummaryProps {
  totalTools: number;
  highRiskTools: number;
  mediumRiskTools: number;
  lowRiskTools: number;
  toolsList?: string[];
  toolsInfo?: Record<string, ToolInfo>;
  onRunOriginAnalysis?: (toolName: string) => void;
}

// Generate origin analysis from actual tool data or fallback to known patterns
const generateOriginAnalysis = (
  toolName: string,
  toolsInfo?: Record<string, ToolInfo>
): OriginAnalysis => {
  // First check if we have actual data for this tool from the scan
  if (toolsInfo && toolsInfo[toolName]) {
    const tool = toolsInfo[toolName];
    const origin = tool.origin || '';
    const isOfficial =
      origin.toLowerCase().includes('claude') ||
      origin.toLowerCase().includes('anthropic') ||
      origin.toLowerCase().includes('core');
    const isHighRisk = tool.riskLevel === 'high';
    const isMediumRisk = tool.riskLevel === 'medium';

    // Calculate security score based on risk level and origin
    let securityScore = 70;
    if (isOfficial) securityScore += 20;
    if (isHighRisk) securityScore -= 30;
    else if (isMediumRisk) securityScore -= 10;
    else securityScore += 10;
    securityScore = Math.max(20, Math.min(95, securityScore));

    return {
      verified: isOfficial,
      source: tool.origin || 'Plugin-provided tool',
      author: isOfficial ? 'Anthropic / Claude Code' : 'Third-party or Community',
      repository: isOfficial ? 'github.com/anthropics/claude-code' : 'See plugin documentation',
      lastUpdated: new Date().toISOString().split('T')[0],
      securityScore,
      knownVulnerabilities: isHighRisk
        ? [
            `${toolName} is classified as HIGH RISK - ${tool.description || 'review capabilities carefully'}`,
          ]
        : [],
      trustIndicators: [
        ...(isOfficial
          ? ['Official Claude Code tool', 'Verified publisher']
          : ['Community/third-party tool']),
        ...(tool.capabilities || []).slice(0, 2).map((c: string) => `Capability: ${c}`),
        `Risk Level: ${tool.riskLevel?.toUpperCase() || 'Unknown'}`,
      ],
    };
  }

  // Fallback to hardcoded analysis for specific known tools
  const analyses: Record<string, OriginAnalysis> = {
    Bash: {
      verified: true,
      source: 'Claude Code Core',
      author: 'Anthropic',
      repository: 'github.com/anthropics/claude-code',
      lastUpdated: new Date().toISOString().split('T')[0],
      securityScore: 55,
      knownVulnerabilities: ['Arbitrary command execution - HIGH RISK'],
      trustIndicators: ['Official Claude Code tool', 'Requires user approval', 'Full shell access'],
    },
    Read: {
      verified: true,
      source: 'Claude Code Core',
      author: 'Anthropic',
      repository: 'github.com/anthropics/claude-code',
      lastUpdated: new Date().toISOString().split('T')[0],
      securityScore: 90,
      knownVulnerabilities: [],
      trustIndicators: ['Official Claude Code tool', 'Read-only access', 'Low risk'],
    },
    Write: {
      verified: true,
      source: 'Claude Code Core',
      author: 'Anthropic',
      repository: 'github.com/anthropics/claude-code',
      lastUpdated: new Date().toISOString().split('T')[0],
      securityScore: 65,
      knownVulnerabilities: ['Can overwrite files - requires caution'],
      trustIndicators: ['Official Claude Code tool', 'File modification capability'],
    },
    WebFetch: {
      verified: true,
      source: 'Claude Code Core',
      author: 'Anthropic',
      repository: 'github.com/anthropics/claude-code',
      lastUpdated: new Date().toISOString().split('T')[0],
      securityScore: 70,
      knownVulnerabilities: ['Potential data exfiltration vector'],
      trustIndicators: ['Official Claude Code tool', 'Network access', 'HTTP requests'],
    },
    WebSearch: {
      verified: true,
      source: 'Claude Code Core',
      author: 'Anthropic',
      repository: 'github.com/anthropics/claude-code',
      lastUpdated: new Date().toISOString().split('T')[0],
      securityScore: 75,
      knownVulnerabilities: ['Search queries may reveal intent'],
      trustIndicators: ['Official Claude Code tool', 'Web search capability'],
    },
    Task: {
      verified: true,
      source: 'Claude Code Core',
      author: 'Anthropic',
      repository: 'github.com/anthropics/claude-code',
      lastUpdated: new Date().toISOString().split('T')[0],
      securityScore: 60,
      knownVulnerabilities: ['Sub-agent spawning may amplify capabilities'],
      trustIndicators: ['Official Claude Code tool', 'Agent spawning', 'Privilege delegation'],
    },
  };

  return (
    analyses[toolName] || {
      verified: false,
      source: 'Unknown',
      author: 'Unknown',
      lastUpdated: new Date().toISOString().split('T')[0],
      securityScore: 50,
      knownVulnerabilities: ['Origin not verified - treat with caution'],
      trustIndicators: [],
    }
  );
};

const MCPToolsSummary = ({
  totalTools,
  highRiskTools,
  mediumRiskTools,
  lowRiskTools,
  toolsList,
  toolsInfo = {},
  onRunOriginAnalysis,
}: MCPToolsSummaryProps) => {
  const [selectedCategory, setSelectedCategory] = useState<RiskLevel | 'all' | null>(null);
  const [selectedTool, setSelectedTool] = useState<ToolInfo | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState<string | null>(null);
  const [analyzedTools, setAnalyzedTools] = useState<Record<string, OriginAnalysis>>({});

  if (totalTools === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-center gap-3 text-muted-foreground">
        <CheckCircle2 className="w-8 h-8 text-green-500 opacity-70" />
        <p className="text-sm font-medium">No MCP tools detected in this scan</p>
        <p className="text-xs max-w-sm">
          If this target exposes MCP services, they will appear here once the scanner discovers
          them.
        </p>
      </div>
    );
  }

  const getRiskStatement = () => {
    if (highRiskTools > 3) {
      return {
        level: 'high' as const,
        message: `This plugin exposes ${totalTools} MCP tools, including ${highRiskTools} high-risk tools that require careful monitoring and restricted access policies.`,
        icon: AlertTriangle,
      };
    } else if (highRiskTools > 0 || mediumRiskTools > 3) {
      return {
        level: 'medium' as const,
        message: `This plugin exposes ${totalTools} MCP tools with ${highRiskTools} high-risk and ${mediumRiskTools} medium-risk tools. Standard security monitoring is recommended.`,
        icon: AlertTriangle,
      };
    } else {
      return {
        level: 'low' as const,
        message: `This plugin exposes ${totalTools} MCP tools, primarily consisting of low-risk read-only operations. Minimal security concerns identified.`,
        icon: CheckCircle,
      };
    }
  };

  const riskStatement = getRiskStatement();
  const RiskIcon = riskStatement.icon;

  const getFilteredTools = (): ToolInfo[] => {
    if (!toolsList) return [];

    return toolsList
      .map((name) => {
        const baseInfo = toolsInfo[name] || {
          name,
          description: 'No description available',
          riskLevel: 'info' as RiskLevel,
        };
        // Attach origin analysis if available
        return {
          ...baseInfo,
          originAnalysis: analyzedTools[name],
        };
      })
      .filter((tool) => {
        if (!selectedCategory || selectedCategory === 'all') return true;
        return tool.riskLevel === selectedCategory;
      });
  };

  const handleCategoryClick = (category: RiskLevel | 'all') => {
    setSelectedCategory(category);
  };

  const handleToolClick = (tool: ToolInfo) => {
    // Include any origin analysis we have
    setSelectedTool({
      ...tool,
      originAnalysis: analyzedTools[tool.name] || tool.originAnalysis,
    });
  };

  const handleRunAnalysis = (toolName: string) => {
    setIsAnalyzing(toolName);
    if (onRunOriginAnalysis) {
      onRunOriginAnalysis(toolName);
    }
    // Analyze tool using actual data from toolsInfo
    setTimeout(() => {
      const analysis = generateOriginAnalysis(toolName, toolsInfo);
      setAnalyzedTools((prev) => ({
        ...prev,
        [toolName]: analysis,
      }));
      // Update selected tool if it's currently open
      if (selectedTool?.name === toolName) {
        setSelectedTool((prev) => (prev ? { ...prev, originAnalysis: analysis } : null));
      }
      setIsAnalyzing(null);
    }, 1000); // Reduced from 2000ms for faster feedback
  };

  const filteredTools = getFilteredTools();

  const getSecurityScoreColor = (score: number) => {
    if (score >= 80) return 'text-emerald-600 bg-emerald-500/10';
    if (score >= 60) return 'text-amber-600 bg-amber-500/10';
    return 'text-red-600 bg-red-500/10';
  };

  return (
    <>
      <div
        className={cn(
          'rounded-xl border p-5 mb-6',
          riskStatement.level === 'high' && 'bg-risk-high-bg border-risk-high/30',
          riskStatement.level === 'medium' && 'bg-risk-medium-bg border-risk-medium/30',
          riskStatement.level === 'low' && 'bg-risk-low-bg border-risk-low/30'
        )}
      >
        <div className="flex items-start gap-4">
          <div
            className={cn(
              'p-2 rounded-lg',
              riskStatement.level === 'high' && 'bg-risk-high/20',
              riskStatement.level === 'medium' && 'bg-risk-medium/20',
              riskStatement.level === 'low' && 'bg-risk-low/20'
            )}
          >
            <Wrench
              className={cn(
                'w-5 h-5',
                riskStatement.level === 'high' && 'text-risk-high',
                riskStatement.level === 'medium' && 'text-risk-medium',
                riskStatement.level === 'low' && 'text-risk-low'
              )}
            />
          </div>

          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <h4 className="font-semibold text-foreground">MCP Tools Analysis</h4>
              <RiskIcon
                className={cn(
                  'w-4 h-4',
                  riskStatement.level === 'high' && 'text-risk-high',
                  riskStatement.level === 'medium' && 'text-risk-medium',
                  riskStatement.level === 'low' && 'text-risk-low'
                )}
              />
            </div>

            <p className="text-sm text-muted-foreground mb-4">{riskStatement.message}</p>

            <p className="text-xs text-muted-foreground mb-3">
              Click any count below to view tools in that risk category
            </p>

            <div className="grid grid-cols-4 gap-4">
              <div
                className={cn(
                  'text-center p-3 rounded-lg cursor-pointer transition-all hover:shadow-md hover:scale-[1.02]',
                  selectedCategory === 'all'
                    ? 'bg-primary text-primary-foreground ring-2 ring-primary'
                    : 'bg-background/50 hover:bg-background'
                )}
                onClick={() => handleCategoryClick('all')}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && handleCategoryClick('all')}
              >
                <div
                  className={cn(
                    'text-2xl font-bold',
                    selectedCategory === 'all' ? 'text-primary-foreground' : 'text-foreground'
                  )}
                >
                  {totalTools}
                </div>
                <div
                  className={cn(
                    'text-xs uppercase tracking-wider',
                    selectedCategory === 'all'
                      ? 'text-primary-foreground/80'
                      : 'text-muted-foreground'
                  )}
                >
                  Total Tools
                </div>
              </div>
              <div
                className={cn(
                  'text-center p-3 rounded-lg cursor-pointer transition-all hover:shadow-md hover:scale-[1.02]',
                  selectedCategory === 'high' ? 'ring-2 ring-risk-high' : '',
                  'bg-risk-high-bg'
                )}
                onClick={() => handleCategoryClick('high')}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && handleCategoryClick('high')}
              >
                <div className="text-2xl font-bold text-risk-high">{highRiskTools}</div>
                <div className="text-xs text-muted-foreground uppercase tracking-wider">
                  High Risk
                </div>
              </div>
              <div
                className={cn(
                  'text-center p-3 rounded-lg cursor-pointer transition-all hover:shadow-md hover:scale-[1.02]',
                  selectedCategory === 'medium' ? 'ring-2 ring-risk-medium' : '',
                  'bg-risk-medium-bg'
                )}
                onClick={() => handleCategoryClick('medium')}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && handleCategoryClick('medium')}
              >
                <div className="text-2xl font-bold text-risk-medium">{mediumRiskTools}</div>
                <div className="text-xs text-muted-foreground uppercase tracking-wider">
                  Medium Risk
                </div>
              </div>
              <div
                className={cn(
                  'text-center p-3 rounded-lg cursor-pointer transition-all hover:shadow-md hover:scale-[1.02]',
                  selectedCategory === 'low' ? 'ring-2 ring-risk-low' : '',
                  'bg-risk-low-bg'
                )}
                onClick={() => handleCategoryClick('low')}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && handleCategoryClick('low')}
              >
                <div className="text-2xl font-bold text-risk-low">{lowRiskTools}</div>
                <div className="text-xs text-muted-foreground uppercase tracking-wider">
                  Low Risk
                </div>
              </div>
            </div>

            {/* Expanded Tools List */}
            {selectedCategory && (
              <div className="mt-6 pt-4 border-t border-border/50 animate-fade-in">
                <div className="flex items-center justify-between mb-4">
                  <h5 className="font-semibold text-sm flex items-center gap-2">
                    <Wrench className="w-4 h-4" />
                    {selectedCategory === 'all'
                      ? 'All Tools'
                      : `${selectedCategory.charAt(0).toUpperCase() + selectedCategory.slice(1)} Risk Tools`}
                    <span className="text-muted-foreground font-normal">
                      ({filteredTools.length})
                    </span>
                  </h5>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedCategory(null)}>
                    <X className="w-4 h-4 mr-1" />
                    Close
                  </Button>
                </div>

                <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
                  {filteredTools.map((tool, index) => {
                    const hasAnalysis = !!analyzedTools[tool.name];
                    return (
                      <div
                        key={index}
                        className="bg-background rounded-lg border p-4 hover:shadow-md transition-all"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <code className="text-sm font-mono font-semibold bg-muted px-2 py-1 rounded">
                              {tool.name}
                            </code>
                            <RiskBadge level={tool.riskLevel} size="sm" />
                            {hasAnalysis && (
                              <Badge
                                variant="outline"
                                className="text-xs bg-emerald-500/10 text-emerald-600 border-emerald-500/20"
                              >
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Analyzed
                              </Badge>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleToolClick(tool)}
                            >
                              <Info className="w-3 h-3 mr-1" />
                              Details
                            </Button>
                            <Button
                              variant="default"
                              size="sm"
                              onClick={() => handleRunAnalysis(tool.name)}
                              disabled={isAnalyzing === tool.name}
                            >
                              <Play className="w-3 h-3 mr-1" />
                              {isAnalyzing === tool.name ? 'Analyzing...' : 'Origin Analysis'}
                            </Button>
                          </div>
                        </div>
                        <p className="text-sm text-muted-foreground">{tool.description}</p>
                        {tool.origin && (
                          <div className="mt-2 text-xs text-muted-foreground">
                            <span className="font-medium">Origin:</span> {tool.origin}
                          </div>
                        )}

                        {/* Show origin analysis summary if available */}
                        {hasAnalysis && (
                          <div className="mt-3 p-3 bg-muted/50 rounded-lg border">
                            <div className="flex items-center gap-4 text-xs">
                              <div className="flex items-center gap-1">
                                {analyzedTools[tool.name].verified ? (
                                  <CheckCircle className="w-3 h-3 text-emerald-500" />
                                ) : (
                                  <AlertTriangle className="w-3 h-3 text-amber-500" />
                                )}
                                <span>
                                  {analyzedTools[tool.name].verified ? 'Verified' : 'Unverified'}
                                </span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Globe className="w-3 h-3" />
                                <span>{analyzedTools[tool.name].source}</span>
                              </div>
                              <div
                                className={cn(
                                  'flex items-center gap-1 px-2 py-0.5 rounded',
                                  getSecurityScoreColor(analyzedTools[tool.name].securityScore)
                                )}
                              >
                                <Shield className="w-3 h-3" />
                                <span>Score: {analyzedTools[tool.name].securityScore}/100</span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {filteredTools.length === 0 && (
                    <p className="text-center text-muted-foreground py-8">
                      No tools found in this category.
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Simple tools list when no category selected */}
            {!selectedCategory && toolsList && toolsList.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-1.5">
                {toolsList.map((tool, index) => {
                  const toolInfo = toolsInfo[tool];
                  const hasAnalysis = !!analyzedTools[tool];
                  return (
                    <div key={index} className="flex items-center">
                      <code
                        className={cn(
                          'text-xs px-2 py-1 rounded font-mono cursor-pointer transition-all hover:scale-105',
                          toolInfo?.riskLevel === 'high' && 'bg-risk-high-bg text-risk-high',
                          toolInfo?.riskLevel === 'medium' && 'bg-risk-medium-bg text-risk-medium',
                          toolInfo?.riskLevel === 'low' && 'bg-risk-low-bg text-risk-low',
                          !toolInfo && 'bg-muted text-muted-foreground',
                          hasAnalysis && 'ring-1 ring-emerald-500/50'
                        )}
                        onClick={() =>
                          toolInfo &&
                          handleToolClick({ ...toolInfo, originAnalysis: analyzedTools[tool] })
                        }
                        role="button"
                        tabIndex={0}
                      >
                        {tool}
                        {hasAnalysis && (
                          <CheckCircle className="w-2 h-2 inline ml-1 text-emerald-500" />
                        )}
                      </code>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tool Detail Dialog */}
      <Dialog open={!!selectedTool} onOpenChange={() => setSelectedTool(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <code className="text-lg font-mono bg-muted px-2 py-1 rounded">
                {selectedTool?.name}
              </code>
              {selectedTool && <RiskBadge level={selectedTool.riskLevel} size="sm" />}
              {selectedTool?.originAnalysis && (
                <Badge
                  variant="outline"
                  className="text-xs bg-emerald-500/10 text-emerald-600 border-emerald-500/20"
                >
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Origin Analyzed
                </Badge>
              )}
            </DialogTitle>
            <DialogDescription>Tool capabilities and origin analysis</DialogDescription>
          </DialogHeader>

          {selectedTool && (
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                  Description
                </h4>
                <p className="text-sm">{selectedTool.description}</p>
              </div>

              {selectedTool.origin && (
                <div>
                  <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                    Origin / Source
                  </h4>
                  <p className="text-sm bg-muted p-3 rounded-lg">{selectedTool.origin}</p>
                </div>
              )}

              {selectedTool.capabilities && selectedTool.capabilities.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                    Capabilities
                  </h4>
                  <ul className="list-disc list-inside space-y-1">
                    {selectedTool.capabilities.map((cap, index) => (
                      <li key={index} className="text-sm">
                        {cap}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Origin Analysis Results */}
              {selectedTool.originAnalysis && (
                <div className="border rounded-lg p-4 bg-muted/30">
                  <h4 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    Origin Analysis Results
                  </h4>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-muted-foreground">
                          Verification:
                        </span>
                        {selectedTool.originAnalysis.verified ? (
                          <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Verified
                          </Badge>
                        ) : (
                          <Badge className="bg-amber-500/10 text-amber-600 border-amber-500/20">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            Unverified
                          </Badge>
                        )}
                      </div>

                      <div>
                        <span className="text-sm font-medium text-muted-foreground block">
                          Source:
                        </span>
                        <span className="text-sm">{selectedTool.originAnalysis.source}</span>
                      </div>

                      <div>
                        <span className="text-sm font-medium text-muted-foreground block">
                          Author:
                        </span>
                        <span className="text-sm">{selectedTool.originAnalysis.author}</span>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div>
                        <span className="text-sm font-medium text-muted-foreground block">
                          Security Score:
                        </span>
                        <div
                          className={cn(
                            'inline-flex items-center gap-1 px-2 py-1 rounded text-sm font-semibold mt-1',
                            getSecurityScoreColor(selectedTool.originAnalysis.securityScore)
                          )}
                        >
                          <Shield className="w-4 h-4" />
                          {selectedTool.originAnalysis.securityScore}/100
                        </div>
                      </div>

                      {selectedTool.originAnalysis.repository && (
                        <div>
                          <span className="text-sm font-medium text-muted-foreground block">
                            Repository:
                          </span>
                          <a
                            href={`https://${selectedTool.originAnalysis.repository}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-primary hover:underline flex items-center gap-1"
                          >
                            {selectedTool.originAnalysis.repository}
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        </div>
                      )}

                      <div className="flex items-center gap-1 text-sm">
                        <Clock className="w-3 h-3 text-muted-foreground" />
                        <span className="text-muted-foreground">Last updated:</span>
                        <span>{selectedTool.originAnalysis.lastUpdated}</span>
                      </div>
                    </div>
                  </div>

                  {/* Trust Indicators */}
                  {selectedTool.originAnalysis.trustIndicators.length > 0 && (
                    <div className="mb-3">
                      <span className="text-sm font-medium text-muted-foreground block mb-2">
                        Trust Indicators:
                      </span>
                      <div className="flex flex-wrap gap-2">
                        {selectedTool.originAnalysis.trustIndicators.map((indicator, index) => (
                          <Badge
                            key={index}
                            variant="outline"
                            className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20"
                          >
                            <CheckCircle className="w-3 h-3 mr-1" />
                            {indicator}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Known Vulnerabilities */}
                  {selectedTool.originAnalysis.knownVulnerabilities.length > 0 && (
                    <div>
                      <span className="text-sm font-medium text-muted-foreground block mb-2">
                        Known Vulnerabilities:
                      </span>
                      <div className="space-y-2">
                        {selectedTool.originAnalysis.knownVulnerabilities.map((vuln, index) => (
                          <div
                            key={index}
                            className="flex items-start gap-2 p-2 bg-red-500/10 rounded border border-red-500/20"
                          >
                            <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-red-700">{vuln}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="pt-4 border-t">
                <Button
                  className="w-full"
                  onClick={() => handleRunAnalysis(selectedTool.name)}
                  disabled={isAnalyzing === selectedTool.name}
                >
                  <Play className="w-4 h-4 mr-2" />
                  {isAnalyzing === selectedTool.name
                    ? 'Running Origin Analysis...'
                    : selectedTool.originAnalysis
                      ? 'Re-run Origin Analysis'
                      : 'Run Origin Analysis'}
                </Button>
                <p className="text-xs text-muted-foreground mt-2 text-center">
                  Analyzes the tool's origin, verifies authenticity, and checks for known
                  vulnerabilities
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MCPToolsSummary;
